"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import NextImage from "next/image";
import { useLanguage } from "@/context/LanguageContext";
import { Globe, Facebook, Linkedin } from "lucide-react";
import { BRAND_ASSETS } from "@/data/assets";
import ContactModal from "../ui/ContactModal";
import homepageService from "@/services/homepageService";

const Logo = React.memo(({ src }: { src?: string }) => (
  <div className="flex-shrink-0 flex items-center group cursor-pointer">
    <img
      alt="AHV Holding Logo"
      className="h-10 w-auto object-contain"
      src={src || "/logo.png"}
    />
  </div>
));

Logo.displayName = "Logo";

const LanguageSwitcher = React.memo(() => {
  const { language, setLanguage } = useLanguage();

  return (
    <div className="flex items-center gap-2 bg-[#0a192f]/50 p-1 rounded-full border border-[#00f3ff]/20">
      <button
        onClick={() => setLanguage("vi")}
        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase transition-all duration-300 ${
          language === "vi"
            ? "bg-[#00f3ff] text-[#040d21] shadow-[0_0_10px_rgba(0,243,255,0.5)]"
            : "text-slate-400 hover:text-[#00f3ff]"
        }`}
      >
        VN
      </button>
      <button
        onClick={() => setLanguage("en")}
        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase transition-all duration-300 ${
          language === "en"
            ? "bg-[#00f3ff] text-[#040d21] shadow-[0_0_10px_rgba(0,243,255,0.5)]"
            : "text-slate-400 hover:text-[#00f3ff]"
        }`}
      >
        EN
      </button>
    </div>
  );
});

LanguageSwitcher.displayName = "LanguageSwitcher";

export const Navbar = ({ cmsData: propCmsData }: { cmsData?: any }) => {
  const { t, language } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cmsData, setCmsData] = useState<any>(propCmsData);

  useEffect(() => {
    if (!propCmsData) {
      const fetchCmsData = async () => {
        try {
          const data = await homepageService.getContent(language);
          if (data && data.navigation) {
            setCmsData(data.navigation);
          }
        } catch (error) {
          console.error("Failed to fetch navbar CMS data:", error);
        }
      };
      fetchCmsData();
    } else {
      setCmsData(propCmsData);
    }
  }, [propCmsData, language]);
  const pathname = usePathname();

  /**
   * Checks if a nav link is the currently active route.
   * Uses exact match for home ("/") and startsWith for sub-routes.
   *
   * @param href - The link's href
   * @returns Whether the link matches the current pathname
   */
  const isActive = (href: string): boolean => {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#040d21]/80 backdrop-blur-xl border-b border-[#00f3ff]/10">
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[100] bg-[#040d21]/95 backdrop-blur-2xl transition-all duration-500 md:hidden ${
          isMenuOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible pointer-events-none"
        }`}
      >
        <div className="flex flex-col h-full p-8 relative">
          {/* Menu Glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#00f3ff]/10 blur-[100px] -z-10" />

          <div className="flex justify-between items-center mb-16">
            <Logo src={cmsData?.logo} />
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2.5 bg-white/5 rounded-xl text-slate-400 hover:text-[#00f3ff] transition-all active:scale-95"
            >
              <span className="material-symbols-outlined text-2xl">close</span>
            </button>
          </div>

          <div className="flex flex-col space-y-6">
            {(cmsData?.links || [
              { href: "/", label: t("common.nav.home") },
              { href: "/ecosystem", label: t("common.nav.ecosystem") },
              { href: "/news", label: t("common.nav.news") },
              { href: "/careers", label: "Tuyển dụng" },
            ]).map((link: any, idx: number) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                className={`text-2xl font-black transition-all flex items-center justify-between group active:scale-[0.98] ${
                  isActive(link.href)
                    ? "text-[#00f3ff] drop-shadow-[0_0_8px_rgba(0,243,255,0.6)]"
                    : "text-white hover:text-[#00f3ff]"
                }`}
                style={{
                  animation: `slideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards ${idx * 0.08}s`,
                  opacity: 0,
                  transform: "translateX(-20px)",
                }}
              >
                <span className="group-hover:translate-x-2 transition-transform duration-500 uppercase tracking-tighter">
                  {link.label}
                </span>
                <span className="material-symbols-outlined text-xl opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#00f3ff]">
                  arrow_forward
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-auto pt-8 border-t border-[#00f3ff]/10 flex flex-col gap-6">
            <div className="flex items-center justify-between">
              {/* <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">
                Ngôn ngữ
              </span>
              <LanguageSwitcher /> */}
            </div>
            <button
              onClick={() => {
                setIsMenuOpen(false);
                setIsContactModalOpen(true);
              }}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-black text-xs uppercase tracking-[0.2em] shadow-[0_0_20px_rgba(0,243,255,0.2)] active:scale-95 transition-transform"
            >
              {cmsData?.cta || t("common.nav.contact")}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link href="/">
            <Logo src={cmsData?.logo} />
          </Link>
          <div className="hidden md:flex space-x-10 items-center">
            {(cmsData?.links || [
              { href: "/", label: t("common.nav.home") },
              { href: "/ecosystem", label: t("common.nav.ecosystem") },
              { href: "/news", label: t("common.nav.news") },
              { href: "/careers", label: "Tuyển dụng" },
            ]).map((link: any) => (
              <Link
                key={link.href}
                href={link.href}
                className={`relative font-semibold text-sm transition-all ${
                  isActive(link.href)
                    ? "text-[#00f3ff] drop-shadow-[0_0_5px_rgba(0,243,255,0.5)]"
                    : "text-slate-300 hover:text-[#00f3ff] hover:drop-shadow-[0_0_5px_currentColor]"
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#00f3ff] shadow-[0_0_6px_#00f3ff]" />
                )}
              </Link>
            ))}

            {/* <LanguageSwitcher /> */}

            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-8 py-3 rounded-full font-bold text-sm hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {cmsData?.cta || t("common.nav.contact")}
            </button>
          </div>
          <div className="md:hidden flex items-center gap-4">
            {/* <LanguageSwitcher /> */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="p-2 text-white hover:text-[#00f3ff] hover:drop-shadow-[0_0_5px_currentColor] transition-all"
            >
              <span className="material-symbols-outlined text-3xl">menu</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export function Footer() {
  const { t, language } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [cmsData, setCmsData] = useState<any>(null);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const data = await homepageService.getContent(language);
        if (data && data.footer) {
          setCmsData(data.footer);
        }
      } catch (error) {
        console.error("Failed to fetch footer CMS data:", error);
      }
    };
    fetchFooterData();
  }, [language]);

  const handleUnimplemented = (e: React.MouseEvent) => {
    e.preventDefault();
    import("react-hot-toast").then((module) => {
      module.default("Tính năng đang trong giai đoạn triển khai", { icon: "🔔" });
    });
  };

  return (
    <footer
      className="bg-brand-dark text-white pt-32 pb-12 relative overflow-hidden"
      data-purpose="site-footer"
      id="lien-he"
    >
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-24">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="text-white font-extrabold text-3xl tracking-tighter mb-8">
              <Logo />
            </div>
            <p className="text-gray-400 mb-10 font-medium italic leading-relaxed text-lg">
              {cmsData?.description || t("home.services.description")}
            </p>
            <div className="flex space-x-5">
              {(cmsData?.socialLinks || [
                { platform: "facebook", url: "https://www.facebook.com/share/1BBWaDB3wy/?mibextid=wwXIfr" },
                { platform: "linkedin", url: "#" }
              ]).map((social: any, idx: number) => (
                <a
                  key={idx}
                  className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-brand-primary hover:-translate-y-1 transition-all"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={social.url === "#" ? handleUnimplemented : undefined}
                  title={social.platform}
                >
                  {social.platform === "facebook" ? <Facebook className="w-6 h-6" /> : <Linkedin className="w-6 h-6" />}
                </a>
              ))}
            </div>
          </div>
          {/* Quick Links */}
          <div>
            <h4 className="text-xl font-extrabold mb-8 text-white">
              {t("common.footer.links")}
            </h4>
            <ul className="space-y-4 text-gray-400 font-medium">
              <li>
                <Link
                  className="hover:text-brand-primary transition-colors"
                  href="/"
                >
                  {t("common.nav.home")}
                </Link>
              </li>
              <li>
                <Link
                  className="hover:text-brand-primary transition-colors"
                  href="/ecosystem"
                >
                  {t("common.nav.ecosystem")}
                </Link>
              </li>
              <li>
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="text-gray-400 hover:text-cyan-400 transition-colors duration-300"
                >
                  {t("common.nav.contact")}
                </button>
              </li>
              <li>
                <Link
                  className="hover:text-brand-primary transition-colors"
                  href="/news"
                >
                  {t("common.nav.news")}
                </Link>
              </li>
              <li>
                <Link
                  className="hover:text-brand-primary transition-colors"
                  href="/careers"
                >
                  Tuyển dụng
                </Link>
              </li>
            </ul>
          </div>
          {/* Offices List */}
          {(cmsData?.offices || [
            { city: "Thái Nguyên", address: t("common.footer.offices.tn"), phone: "(+84) 393 797 791", email: "hr@ahvholding.com" },
            { city: "Hà Nội", address: t("common.footer.offices.hn"), phone: "(+84) 24 987 654", email: "hn@ahvholding.com" }
          ]).map((office: any, idx: number) => (
            <div key={idx}>
              <h4 className="text-xl font-extrabold mb-8 text-white">
                {office.city}
              </h4>
              <div className="text-gray-400 font-medium leading-loose">
                <p>{office.address}</p>
                <p className="mt-4">
                  Hotline: <span className="text-white">{office.phone}</span>
                </p>
                <p>
                  Email: <span className="text-white">{office.email}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between gap-4 text-slate-400 text-xs font-medium">
          <p>{cmsData?.copyright || t("common.footer.copyright")}</p>
          <div className="flex gap-6">
            <Link className="hover:text-slate-200 transition-colors" href="/privacy">
              {t("common.footer.privacy")}
            </Link>
            <Link className="hover:text-slate-200 transition-colors" href="/terms">
              {t("common.footer.terms")}
            </Link>
            <Link className="hover:text-slate-200 transition-colors" href="/cookies">
              {t("common.footer.cookies")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
